import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile, updatePassword, updateEmail } from 'firebase/auth';
import { db, storage, auth } from '../firebaseConfig';

/**
 * Get user profile from Firestore
 */
export const getUserProfile = async (userId) => {
  try {
    const docRef = doc(db, 'users', userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      // Create default profile if it doesn't exist
      const defaultProfile = {
        displayName: auth.currentUser?.email?.split('@')[0] || 'User',
        email: auth.currentUser?.email || '',
        photoURL: '',
        websiteUrl: '',
        githubUrl: '',
        bio: '',
        scopeFilter: 'all', // all, organization, personal
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await setDoc(docRef, defaultProfile);
      return defaultProfile;
    }
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

/**
 * Update user profile in Firestore and Firebase Auth
 */
export const updateUserProfile = async (userId, profileData) => {
  try {
    const docRef = doc(db, 'users', userId);
    const updateData = {
      ...profileData,
      updatedAt: new Date().toISOString(),
    };
    
    await updateDoc(docRef, updateData);
    
    // Update Firebase Auth profile if displayName or photoURL changed
    if (profileData.displayName || profileData.photoURL) {
      const authUpdateData = {};
      if (profileData.displayName) authUpdateData.displayName = profileData.displayName;
      if (profileData.photoURL) authUpdateData.photoURL = profileData.photoURL;
      await updateProfile(auth.currentUser, authUpdateData);
    }
    
    return true;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

/**
 * Upload profile picture to Firebase Storage
 */
export const uploadProfilePicture = async (userId, imageUri) => {
  try {
    // Convert image URI to blob
    const response = await fetch(imageUri);
    const blob = await response.blob();
    
    // Create storage reference
    const storageRef = ref(storage, `profilePictures/${userId}/${Date.now()}.jpg`);
    
    // Upload file
    await uploadBytes(storageRef, blob);
    
    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);
    
    // Update profile with new photo URL
    await updateUserProfile(userId, { photoURL: downloadURL });
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading profile picture:', error);
    throw error;
  }
};

/**
 * Change user password
 */
export const changeUserPassword = async (newPassword) => {
  try {
    await updatePassword(auth.currentUser, newPassword);
    return true;
  } catch (error) {
    console.error('Error changing password:', error);
    throw error;
  }
};

/**
 * Change user email
 */
export const changeUserEmail = async (newEmail) => {
  try {
    await updateEmail(auth.currentUser, newEmail);
    
    // Update Firestore profile
    const userId = auth.currentUser.uid;
    await updateUserProfile(userId, { email: newEmail });
    
    return true;
  } catch (error) {
    console.error('Error changing email:', error);
    throw error;
  }
};

/**
 * Get user's scope filter preference
 * @returns {string} 'all', 'organization', or 'personal'
 */
export const getUserScopeFilter = async (userId) => {
  try {
    const profile = await getUserProfile(userId);
    return profile.scopeFilter || 'all';
  } catch (error) {
    console.error('Error getting scope filter:', error);
    return 'all'; // Default fallback
  }
};

/**
 * Update user's scope filter preference
 * @param {string} userId - User ID
 * @param {string} filter - 'all', 'organization', or 'personal'
 */
export const updateUserScopeFilter = async (userId, filter) => {
  try {
    await updateUserProfile(userId, { scopeFilter: filter });
    return true;
  } catch (error) {
    console.error('Error updating scope filter:', error);
    throw error;
  }
};

